import { isMobileBreakpoint } from './utilities.js';

const ZOOM_SCALE = 2.5;

/**
 * Click-to-toggle inline zoom for product images on desktop.
 * Click once to zoom in at the clicked point; move cursor to pan.
 * Click again or move cursor out to zoom out.
 * No-ops on mobile breakpoints (handled by touch scrolling).
 */
export class ProductZoom extends HTMLElement {
  #controller = new AbortController();
  #active = false;

  get #image() {
    return this.querySelector('img.product-media__image');
  }

  connectedCallback() {
    if (isMobileBreakpoint()) return;

    const { signal } = this.#controller;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('mousemove', this.#onMove, { signal });
    this.addEventListener('mouseleave', this.#onLeave, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #onClick = (event) => {
    if (this.#active) {
      this.#deactivate();
    } else {
      this.#activate(event);
    }
  };

  #onMove = (event) => {
    if (!this.#active) return;
    this.#applyZoom(event);
  };

  #onLeave = () => {
    if (!this.#active) return;
    this.#deactivate();
  };

  #activate(event) {
    const img = this.#image;
    if (!img) return;
    this.#active = true;
    this.style.cursor = 'zoom-out';
    img.style.transition = 'none';
    this.#applyZoom(event);
  }

  #deactivate() {
    this.#active = false;
    this.style.cursor = '';
    const img = this.#image;
    if (!img) return;
    img.style.transition = 'transform 0.25s ease';
    img.style.transform = '';
  }

  /**
   * Apply scale + translate so the point under the cursor stays fixed.
   *
   * For transform: scale(S) translate(tx, ty) with transform-origin at
   * element center (default 50% 50%), the translate required to keep
   * cursor point (cx, cy) stationary is:
   *   tx = (cx - W/2) * (1 - S) / S
   *   ty = (cy - H/2) * (1 - S) / S
   */
  #applyZoom(event) {
    const img = this.#image;
    if (!img) return;

    const rect = this.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    const y = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
    const S = ZOOM_SCALE;

    const tx = (x - rect.width / 2) * (1 - S) / S;
    const ty = (y - rect.height / 2) * (1 - S) / S;

    img.style.transform = `scale(${S}) translate(${tx}px, ${ty}px)`;
  }
}

if (!customElements.get('product-zoom')) {
  customElements.define('product-zoom', ProductZoom);
}
